/**
 * The "where am I, and how do I leave" strip.
 *
 * Getting home used to mean clicking the wordmark or knowing Alt+H.
 * Both work; neither is discoverable, and the people who got lost were
 * the clerks, not the owner. An unlabelled logo is a convention you
 * either already know or you don't.
 *
 * Chart of accounts, Settings and the transport preview had no way back
 * at all. Rather than editing every screen -- and missing the next one
 * someone adds -- the bar lives in the layout and reads the route, so a
 * new page is covered the moment it has a title here.
 *
 * It is deliberately quiet: one line, paper-white, a hairline under it.
 * The figures below are what the user came to read.
 */
import { Box, Button, Stack, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNew'
import HomeIcon from '@mui/icons-material/HomeOutlined'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSmartBack } from '../hooks/useSmartBack'
import { useGuard } from '../store/UnsavedChanges'
import { c } from '../theme'
import Keycap from './Keycap'

/**
 * Route -> title key. Longest match wins, so '/preview/transport' is not
 * swallowed by a shorter prefix.
 *
 * A route absent from here renders the bar with no title rather than
 * hiding it: losing the title is cosmetic, losing Back is not.
 */
const TITLES: [string, string][] = [
  ['/daybook', 'nav.dayBook'],
  ['/outstanding', 'nav.outstanding'],
  ['/ledger', 'nav.ledger'],
  ['/trial-balance', 'nav.trialBalance'],
  ['/balance-sheet', 'nav.balanceSheet'],
  ['/profit-loss', 'nav.profitLoss'],
  ['/sales-register', 'nav.salesRegister'],
  ['/purchase-register', 'nav.purchaseRegister'],
  ['/cash-book', 'nav.cashBook'],
  ['/bank-book', 'nav.bankBook'],
  ['/vat-return', 'nav.vatReturn'],
  ['/transport/profitability', 'nav.vehicleProfit'],
  ['/accounts', 'nav.chartOfAccounts'],
  ['/settings', 'nav.settings'],
  ['/transport/trips', 'nav.tripEntry'],
  ['/transport/invoicing', 'nav.tripInvoicing'],
  ['/transport/fleet', 'nav.fleet'],
  ['/transport/register', 'nav.tripRegister'],
  ['/inventory/items', 'nav.items'],
  ['/inventory/purchases', 'nav.purchases'],
  ['/inventory/sales', 'nav.invSales'],
  ['/inventory/sales/new', 'inv.newSale'],
  ['/inventory/purchases/new', 'inv.newPurchase'],
  ['/inventory/stores', 'nav.stores'],
  ['/inventory/opening', 'nav.openingStock'],
  ['/inventory/transfers', 'nav.stockMoves'],
  ['/inventory/transfers/new', 'stock.newTransfer'],
  ['/inventory/counts', 'nav.stockMoves'],
  ['/inventory/counts/new', 'stock.newCount'],
  ['/inventory/stock', 'nav.stockOnHand'],
  ['/inventory/stock-card', 'nav.stockCard'],
  ['/inventory/van-stock', 'nav.vanStock'],
  ['/inventory/sales-by-item', 'nav.salesByItem'],
  ['/inventory/reorder', 'nav.lowStock'],
  ['/inventory/van-phones', 'nav.vanPhones'],
]

export function titleKeyFor(pathname: string): string | null {
  const voucher = pathname.match(/^\/vouchers\/([a-z]+)/)
  if (voucher) return `voucher.${voucher[1]}`
  const hit = TITLES
    .filter(([p]) => pathname === p || pathname.startsWith(`${p}/`))
    .sort((a, b) => b[0].length - a[0].length)[0]
  return hit ? hit[1] : null
}

export default function PageBar() {
  const { t } = useTranslation()
  const nav = useNavigate()
  const loc = useLocation()
  const { back } = useSmartBack()
  const guard = useGuard()

  // The home screen is the destination, so it needs neither control.
  if (loc.pathname === '/') return null

  const key = titleKeyFor(loc.pathname)

  return (
    /*
     * Deliberately not sticky. It would have to sit at a fixed offset
     * below the app bar, and that offset is not fixed: the app bar grows
     * by the safe-area inset on a notched phone, and the offline banner
     * is another sticky element above it. A bar pinned to the wrong
     * number looks broken; one that scrolls away costs the user nothing,
     * since Escape and Alt+H keep working wherever they are.
     */
    <Box sx={{ borderBottom: `1px solid ${c.border}`, bgcolor: c.surface,
               '@media print': { display: 'none' } }}>
      <Stack direction="row" alignItems="center" spacing={0.5}
        sx={{ px: { xs: 1, sm: 2, md: 3 }, py: 0.5, maxWidth: 1360, mx: 'auto' }}>
        {/* Two buttons that mean different things must not read as one
            phrase. The icons carry the distinction before the words do. */}
        <Button size="small" onClick={() => guard(back)}
          startIcon={<ArrowBackIcon sx={{ fontSize: '0.7rem !important' }} />}
          sx={{ color: 'text.secondary', fontWeight: 500, minWidth: 0,
                textTransform: 'capitalize' }}>
          {t('common.back')}
        </Button>

        <Button size="small" onClick={() => guard(() => nav('/'))}
          startIcon={<HomeIcon sx={{ fontSize: '1rem !important' }} />}
          sx={{ color: 'text.secondary', fontWeight: 500, minWidth: 0,
                ml: 1, textTransform: 'capitalize' }}>
          {t('nav.home')}
        </Button>

        {key && (<>
          <Box sx={{ width: '1px', height: 16, bgcolor: c.border, mx: 1.5 }} />
          <Typography sx={{
            fontSize: 13, fontWeight: 600, color: c.ink,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {t(key)}
          </Typography>
        </>)}

        <Box sx={{ flex: 1 }} />

        {/* Shown, not hidden: this is how a clerk learns the shortcut
            exists at all. */}
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 0.75,
                   alignItems: 'center' }}>
          <Keycap combo="escape" />
          <Keycap combo="alt+h" />
        </Box>
      </Stack>
    </Box>
  )
}
